<template>
  <div>
    <Menu />

    <section>
      <div class="container-fluid pt-5 mt-5 g-0">
        <div class="row g-0 text-center">
          <img src="/images/team-banner-june-update.jpg" class="img-fluid" alt="Luxury Winelands team">
        </div>
      </div>
    </section>

    <section>
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12 text-center pt-5 pb-5">
            <h2>Our Agents</h2>
            <p>We're excited to help you achieve your property dreams and discover your perfect home and idyllic lifestyle.</p>
          </div>
        </div>
      </div>
    </section>

    <section v-if="loading">
      <div class="container pb-5">
        <div class="row">
          <div class="col-12 text-center">
            <p>Loading agents…</p>
          </div>
        </div>
      </div>
    </section>

    <section v-else-if="error">
      <div class="container pb-5">
        <div class="row">
          <div class="col-12 text-center">
            <p>{{ error }}</p>
          </div>
        </div>
      </div>
    </section>

    <template v-else-if="agents.length">
      <section v-if="leadAgent">
        <div class="container">
          <div class="row justify-content-center mb-5">
            <div class="container-fluid bg-light">
              <div class="row">
                <div class="col-md-3 p-3">
                  <img
                    :src="agentImage(leadAgent)"
                    class="img img-fluid"
                    :alt="agentName(leadAgent)"
                    @error="useFallbackImage"
                  >
                </div>
                <div class="col-md-9 p-3">
                  <div class="card-body text-start">
                    <p v-if="leadAgent.profile_url">
                      <a :href="leadAgent.profile_url" target="_blank" rel="noopener noreferrer"><strong>{{ agentTitle(leadAgent) }}</strong></a>
                    </p>
                    <p v-else><strong>{{ agentTitle(leadAgent) }}</strong></p>
                    <p v-if="whatsappLink(leadAgent.cell_number)">
                      <a :href="whatsappLink(leadAgent.cell_number)" target="_blank" rel="noopener noreferrer"><i class="bi bi-whatsapp"></i> WhatsApp</a>
                    </p>
                    <p v-if="leadAgent.cell_number">
                      <a :href="'tel:' + leadAgent.cell_number"><i class="bi bi-telephone-fill"></i> {{ leadAgent.cell_number }}</a>
                    </p>
                    <p v-if="leadAgent.office_number">
                      <a :href="'tel:' + leadAgent.office_number"><i class="bi bi-telephone-fill"></i> {{ leadAgent.office_number }}</a>
                    </p>
                    <p v-if="leadAgent.email_address">
                      <a :href="'mailto:' + leadAgent.email_address"><i class="bi bi-envelope-fill"></i> {{ leadAgent.email_address }}</a>
                    </p>
                    <p v-if="leadAgent.bio">{{ leadAgent.bio }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="otherAgents.length">
        <div class="container pb-5">
          <div class="row justify-content-center">
            <div
              v-for="agent in otherAgents"
              :key="agent._recordKey"
              class="col-md-3 p-3"
            >
              <div class="card">
                <img
                  :src="agentImage(agent)"
                  class="card-img-top"
                  :alt="agentName(agent)"
                  @error="useFallbackImage"
                >
                <div class="card-body text-start">
                  <p v-if="agent.profile_url">
                    <a :href="agent.profile_url" target="_blank" rel="noopener noreferrer"><strong>{{ agentTitle(agent) }}</strong></a>
                  </p>
                  <p v-else><strong>{{ agentTitle(agent) }}</strong></p>
                  <p v-if="whatsappLink(agent.cell_number)">
                    <a :href="whatsappLink(agent.cell_number)" target="_blank" rel="noopener noreferrer"><i class="bi bi-whatsapp"></i> WhatsApp</a>
                  </p>
                  <p v-if="agent.cell_number">
                    <a :href="'tel:' + agent.cell_number"><i class="bi bi-telephone-fill"></i> {{ agent.cell_number }}</a>
                  </p>
                  <p v-if="agent.office_number">
                    <a :href="'tel:' + agent.office_number"><i class="bi bi-telephone-fill"></i> {{ agent.office_number }}</a>
                  </p>
                  <p v-if="agent.email_address">
                    <a :href="'mailto:' + agent.email_address"><i class="bi bi-envelope-fill"></i> {{ agent.email_address }}</a>
                  </p>
                  <p v-if="agent.bio">{{ agent.bio }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <section v-else>
      <div class="container pb-5">
        <div class="row">
          <div class="col-12 text-center">
            <p>No agents are currently available.</p>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Menu from '@/components/Menu.vue'
import Footer from '@/components/Footer.vue'
import {
  apiErrorMessage,
  getAgentData,
  listManagedRecords
} from '@/services/winelandsApi'

const FALLBACK_AGENT_IMAGE = '/images/logos/ce-logo.png'

function agentName(agent) {
  return [agent.first_name, agent.last_name].filter(Boolean).join(' ') || 'Winelands agent'
}

function whatsappLink(number) {
  let digits = String(number || '').replace(/\D/g, '')
  if (!digits) return ''
  if (digits.charAt(0) === '0') digits = '27' + digits.substring(1)
  return 'https://wa.me/' + digits
}

export default {
  name: 'Team',

  components: {
    Menu,
    Footer
  },

  data: function () {
    return {
      agents: [],
      loading: true,
      error: ''
    }
  },

  computed: {
    leadAgent: function () {
      if (!this.agents.length) return null
      return this.agents.find(function (agent) {
        return /principal|owner/i.test(String(agent.job_title || ''))
      }) || this.agents[0]
    },

    otherAgents: function () {
      if (!this.leadAgent) return []
      const leadKey = this.leadAgent._recordKey
      return this.agents.filter(function (agent) {
        return agent._recordKey !== leadKey
      })
    }
  },

  mounted: function () {
    this.loadAgents()
  },

  methods: {
    loadAgents: async function () {
      this.loading = true
      this.error = ''

      try {
        const records = await listManagedRecords('agent')
        this.agents = records.map(function (record, index) {
          return Object.assign({}, getAgentData(record), {
            _recordKey: record.ref_id || record.id || 'agent-' + index
          })
        }).filter(function (agent) {
          return String(agent.status || 'Active').toLowerCase() === 'active'
        })
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not load the agent profiles.')
      } finally {
        this.loading = false
      }
    },

    agentName: function (agent) {
      return agentName(agent)
    },

    agentTitle: function (agent) {
      return [agentName(agent), agent.job_title].filter(Boolean).join(' - ')
    },

    whatsappLink: function (number) {
      return whatsappLink(number)
    },

    agentImage: function (agent) {
      return agent.agent_photo || FALLBACK_AGENT_IMAGE
    },

    useFallbackImage: function (event) {
      if (event.target.getAttribute('src') !== FALLBACK_AGENT_IMAGE) {
        event.target.setAttribute('src', FALLBACK_AGENT_IMAGE)
      }
    }
  }
}
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0px;
}

.card-body a {
  text-decoration: none;
  color: #124e83;
}

.card-body p {
  color: #124e83;
}

.card-body strong {
  color: #124e83;
}
</style>
